import type { Plan, PlanSection, PlanTier } from "types/Plan";
import type { MLTABenefit } from "store/application/applicationSlice";

/**
 * Valid tier names from the MLTA quote API (Pascal case to match backend API)
 */
const VALID_TIERS = ["Value", "Economy", "Deluxe", "Ultra", "Superior", "Premier"] as const;

/**
 * Formats a tier string to title case (e.g., "SUPERIOR" -> "Superior")
 */
export const formatTierTitle = (tier: string | undefined): string => {
    if (!tier) return "";
    return tier.charAt(0).toUpperCase() + tier.slice(1).toLowerCase();
};

/**
 * Formats coverage type to display string
 */
export const formatCoverageType = (coverageType: string | undefined): string => {
    return coverageType === "Family" ? "Family Coverage" : "Individual Coverage";
};

/**
 * Generates the MLTA description string (e.g., "Superior Family Coverage")
 * @param tier - The tier name (e.g., "Superior")
 * @param coverageType - The coverage type (e.g., "Family" or "Individual")
 * @param fallback - Optional fallback string when tier is missing; otherwise returns ""
 */
export const getMLTADescription = (tier: string | undefined, coverageType: string | undefined, fallback?: string): string => {
    const tierTitle = formatTierTitle(tier);
    if (!tierTitle) return fallback ?? "";
    return `${tierTitle} ${formatCoverageType(coverageType)}`;
};

/**
 * Transforms raw API benefit response to MLTABenefit interface
 * Maps snake_case API fields to camelCase
 */
export function transformApiBenefitToMLTABenefit(apiBenefit: {
    tier?: string;
    level?: number;
    airline?: number;
    common?: number;
    loss_of_life?: number;
    hospital?: number;
    yearly_er?: number;
    all_accident?: number;
    individual?: number;
    family?: number;
    correctKeycode?: string;
}): MLTABenefit | null {
    // Validate required fields exist
    if (
        apiBenefit.tier === undefined ||
        apiBenefit.level === undefined ||
        apiBenefit.airline === undefined ||
        apiBenefit.common === undefined ||
        apiBenefit.loss_of_life === undefined ||
        apiBenefit.hospital === undefined ||
        apiBenefit.yearly_er === undefined ||
        apiBenefit.all_accident === undefined ||
        apiBenefit.individual === undefined ||
        apiBenefit.family === undefined ||
        apiBenefit.correctKeycode === undefined
    ) {
        return null;
    }

    return {
        tier: apiBenefit.tier,
        level: apiBenefit.level,
        airline: apiBenefit.airline,
        common: apiBenefit.common,
        lossOfLife: apiBenefit.loss_of_life,
        hospital: apiBenefit.hospital,
        emergencyRoom: apiBenefit.yearly_er,
        allAccident: apiBenefit.all_accident,
        individual: apiBenefit.individual,
        family: apiBenefit.family,
        correctKeycode: apiBenefit.correctKeycode,
    };
}

/**
 * Formats a dollar amount for display
 * Shows 0 decimals for whole numbers, 2 decimals for values with cents
 */
function formatCurrency(amount: number): string {
    const hasDecimals = amount % 1 !== 0;
    const fractionDigits = hasDecimals ? 2 : 0;
    return amount.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
    });
}

/**
 * Creates the benefit sections for a Plan based on MLTA benefit data
 *
 * Section structure:
 * 1. Vehicle Accident - hospital (Hospital icon), yearly_er (Emergency icon)
 * 2. Other Accidents - all_accident (Fall icon), all_accident (Bruised icon)
 * 3. Loss of Life - airline (Airplane icon), common (Bike icon), loss_of_life (Heart icon)
 */
function createPlanSections(benefit: MLTABenefit): PlanSection[] {
    return [
        {
            title: "Vehicle Accident",
            benefits: [
                {
                    icon: "Hospital",
                    amount: `${formatCurrency(benefit.hospital)}/day`,
                    description: "Hospitalization and recovery",
                },
                {
                    icon: "Emergency",
                    amount: `${formatCurrency(benefit.emergencyRoom)}/year`,
                    description: "Vehicle related emergency or urgent care visit.",
                },
            ],
        },
        {
            title: "Other Accidents",
            benefits: [
                {
                    icon: "Fall",
                    amount: `${formatCurrency(benefit.allAccident)}/day`,
                    description: "Hospitalization and recovery",
                },
                {
                    icon: "Bruised",
                    amount: `${formatCurrency(benefit.allAccident)}/year`,
                    description: "Emergency or urgent care visit",
                },
            ],
        },
        {
            title: "Loss of Life",
            benefits: [
                {
                    icon: "Bike",
                    amount: formatCurrency(benefit.common),
                    description: "Vehicle related accident",
                },
                {
                    icon: "Airplane",
                    amount: formatCurrency(benefit.airline),
                    description: "Airline accident",
                },
                {
                    icon: "Heart",
                    amount: formatCurrency(benefit.lossOfLife),
                    description: "Any other accident",
                },
            ],
        },
    ];
}

/**
 * Converts tier string to valid PlanTier type (Pascal case)
 * Returns Pascal case tier name if valid, otherwise returns "Economy" as fallback
 */
export function toValidTier(tier: string): PlanTier {
    // Normalize to Pascal case for comparison
    const pascalTier = tier.charAt(0).toUpperCase() + tier.slice(1).toLowerCase();
    if (VALID_TIERS.includes(pascalTier as PlanTier)) {
        return pascalTier as PlanTier;
    }
    return "Economy";
}

/**
 * Transforms an MLTABenefit into a Plan object for display
 */
export function transformBenefitToPlan(benefit: MLTABenefit): Plan {
    const tierId = toValidTier(benefit.tier);

    return {
        id: tierId,
        name: benefit.tier.charAt(0).toUpperCase() + benefit.tier.slice(1).toLowerCase(),
        monthlyPrice: benefit.individual,
        familyCoveragePrice: benefit.family,
        sections: createPlanSections(benefit),
    };
}

/**
 * Extracts plans from benefits array
 * Returns Plan objects for the first two tiers found (for display as left/right cards)
 */
export function extractPlansFromBenefits(benefits: MLTABenefit[]): {
    primaryPlan: Plan | null;
    secondaryPlan: Plan | null;
} {
    const plans = benefits.map(transformBenefitToPlan);

    return {
        primaryPlan: plans[0] ?? null,
        secondaryPlan: plans[1] ?? null,
    };
}

/**
 * Gets the selected benefit from the benefits array based on tier name
 */
export function getSelectedBenefit(benefits: MLTABenefit[], tierName: string): MLTABenefit | undefined {
    return benefits.find((b) => b.tier.toLowerCase() === tierName.toLowerCase());
}
